import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
    Box,
    List,
    ListItem,
    Paper,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useResetPasswordMutation } from '../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { CustomIconButton } from '../../../layout/styledcomponents/CustomIconButton';
import { CustomTextField } from '../../../layout/styledcomponents/CustomTextField';
import {
    ANDROID_PLAY_STORE_URL,
    IOS_APP_STORE_URL,
} from '../../../shared/appDeepLink';
import toast from '../../shared/Toast';

const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export default function ResetPasswordPage() {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const [resetPassword] = useResetPasswordMutation();
    const queryParameters = new URLSearchParams(window.location.search);
    const emailAddress = `${queryParameters.get('email')}`;
    const sourceParam = queryParameters.get('source');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isValidInput, setIsValidInput] = useState(false);
    const [passwordsDontMatch, setPasswordsDontMatch] = useState(false);
    const [lowerCaseValidation, setLowercaseValidation] = useState(false);
    const [upperCaseValidation, setUppercaseValidation] = useState(false);
    const [pwLengthValidation, setPWLengthValidation] = useState(false);
    const [numberValidation, setNumberValidation] = useState(false);
    const [characterValidation, setCharacterValidation] = useState(false);

    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const path = window.location.href.split('?')[0];
    const token = path.split('/')[4];

    const theme = useTheme();

    const btnstyle = {
        margin: '8px 0px  20px  0px',
        height: '50px',
        fontSize: '16px',
        borderRadius: '5px',
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const errors = [];

    if (emailAddress.length === 0) errors.push('Email cannot be empty');
    if (password.length === 0) errors.push('New password cannot be empty');
    if (confirmPassword.length === 0)
        errors.push('Confirm password cannot be empty');

    const validatEmail = (email: string) => {
        return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
    };
    const isValidEmail = validatEmail(emailAddress);

    const handlePassword = (event: {
        target: { value: React.SetStateAction<string> };
    }) => {
        setPassword(event.target.value);
        if (validatePassword(event.target.value) === false)
            setIsValidInput(true);
        else setIsValidInput(false);
    };

    const handleConfirmPassword = (event: {
        target: { value: React.SetStateAction<string> };
    }) => {
        setConfirmPassword(event.target.value);
        if (password === event.target.value) setPasswordsDontMatch(false);
        else setPasswordsDontMatch(true);
    };

    const validatePassword = (pw: any) => {
        setUppercaseValidation(/(?=.*[A-Z])/.test(pw) === true);
        setPWLengthValidation(pw.length >= 8 === true);
        setLowercaseValidation(/[a-z]/.test(pw) === true);
        setCharacterValidation(/[#?!@$%^&*-]/.test(pw) === true);
        setNumberValidation(/[0-9]/.test(pw) === true);

        return (
            /[A-Z]/.test(pw) &&
            pw.length >= 8 &&
            /[a-z]/.test(pw) &&
            /[0-9]/.test(pw) &&
            /[#?!@$%^&*-]/.test(pw)
        );
    };

    const navigate = useNavigate();

    const onClickResetPassword = () => {
        const requestBody = {
            email: emailAddress,
            token: token,
            newPassword: password,
            confirmNewPassword: confirmPassword,
        };

        resetPassword(requestBody).then((data: any) => {
            if (data) {
                if (data?.error?.data !== 'Password is reset.') {
                    if (data?.error?.data?.message === 'invalid token') {
                        toast.error(`Invalid Token/Token Expired`);
                    } else {
                        toast.error(`Failed to reset password`);
                    }
                } else {
                    toast.success(`Password has been reset`);
                    navigate(`/login`);
                }
            } else {
                toast.error(`Failed to reset password`);
            }
        });
    };

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100vw',
                minHeight: 'var(--app-viewport-height)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: theme.palette.custom.loggedOutBackgroundImage,
                backgroundAttachment: { xs: 'scroll', md: 'fixed' },
                backgroundSize: 'cover',
                py: { xs: 2, sm: 0 },
                px: { xs: 1.5, sm: 0 },
                overflowY: 'auto',
            }}
        >
            <Paper
                elevation={10}
                sx={{
                    width: '100%',
                    maxWidth: 480,
                    borderRadius: 2,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: { xs: 2.5, sm: 4 },
                        px: { xs: 2, sm: 4 },
                    }}
                >
                    <img
                        alt='primarylogo'
                        src={theme.palette.custom.lightLogo}
                        style={{
                            width: '170px',
                            height: 'auto',
                        }}
                    />
                </Box>

                <Box sx={{ px: { xs: 2, sm: 5 }, pb: { xs: 2.5, sm: 4 } }}>
                    <Typography
                        component='h1'
                        variant='h5'
                        sx={{
                            fontSize: { xs: '24px', sm: '28px' },
                            letterSpacing: '-0.5px',
                            pt: 1,
                            pb: 2,
                            px: 0.5,
                        }}
                    >
                        {sourceParam === 'invite'
                            ? 'Set Password'
                            : 'Reset Password'}
                    </Typography>

                    <Typography
                        sx={{
                            mb: 2,
                            px: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <EmailIcon fontSize='small' />
                        {emailAddress}
                    </Typography>

                    <CustomTextField
                        error={isValidInput}
                        label={isValidInput === true ? 'Invalid Password' : ''}
                        placeholder='New Password'
                        type={showNewPassword ? 'text' : 'password'}
                        onChange={handlePassword}
                        sx={{ mb: 1.25 }}
                        color={isValidInput ? 'green' : 'dark'}
                        fullWidth
                        InputProps={{
                            style: { height: '50px' },
                            startAdornment: (
                                <InputAdornment
                                    position='start'
                                    sx={{ tabIndex: '-1' }}
                                >
                                    <LockIcon
                                        fontSize='small'
                                        sx={{ tabIndex: '-1' }}
                                    />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment
                                    position='end'
                                    sx={{ tabIndex: '-1' }}
                                >
                                    {!showNewPassword ? (
                                        <CustomIconButton
                                            tabIndex='-1'
                                            onClick={() =>
                                                setShowNewPassword(
                                                    !showNewPassword,
                                                )
                                            }
                                            sx={{
                                                width: '100%',
                                                height: '25px',
                                                display: 'flex',
                                                '&:hover': {
                                                    cursor: 'pointer',
                                                    color: theme.palette
                                                        .textAccent.main,
                                                    backgroundColor:
                                                        'transparent',
                                                    background: 'none',
                                                },
                                            }}
                                        >
                                            <VisibilityIcon fontSize='small' />
                                        </CustomIconButton>
                                    ) : (
                                        <CustomIconButton
                                            tabIndex='-1'
                                            onClick={() =>
                                                setShowNewPassword(
                                                    !showNewPassword,
                                                )
                                            }
                                            sx={{
                                                width: '100%',
                                                height: '25px',
                                                display: 'flex',
                                                '&:hover': {
                                                    cursor: 'pointer',
                                                    color: theme.palette
                                                        .textAccent.main,
                                                    backgroundColor:
                                                        'transparent',
                                                    background: 'none',
                                                },
                                            }}
                                        >
                                            <VisibilityOffIcon fontSize='small' />
                                        </CustomIconButton>
                                    )}
                                </InputAdornment>
                            ),
                        }}
                    />

                    <CustomTextField
                        placeholder='Confirm Password'
                        error={passwordsDontMatch}
                        label={
                            passwordsDontMatch === true
                                ? 'Passwords do not match'
                                : ''
                        }
                        type={showConfirmPassword ? 'text' : 'password'}
                        onChange={handleConfirmPassword}
                        sx={{ mb: 1.25 }}
                        color={passwordsDontMatch ? 'green' : 'dark'}
                        fullWidth
                        InputProps={{
                            style: { height: '50px' },
                            startAdornment: (
                                <InputAdornment
                                    position='start'
                                    sx={{ tabIndex: '-1' }}
                                >
                                    <LockIcon fontSize='small' />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment
                                    position='end'
                                    sx={{ tabIndex: '-1' }}
                                >
                                    {!showConfirmPassword ? (
                                        <CustomIconButton
                                            tabIndex='-1'
                                            onClick={() =>
                                                setShowConfirmPassword(
                                                    !showConfirmPassword,
                                                )
                                            }
                                            sx={{
                                                width: '100%',
                                                height: '25px',
                                                display: 'flex',
                                                '&:hover': {
                                                    cursor: 'pointer',
                                                    color: theme.palette
                                                        .textAccent.main,
                                                    backgroundColor:
                                                        'transparent',
                                                    background: 'none',
                                                },
                                            }}
                                        >
                                            <VisibilityIcon fontSize='small' />
                                        </CustomIconButton>
                                    ) : (
                                        <CustomIconButton
                                            onClick={() =>
                                                setShowConfirmPassword(
                                                    !showConfirmPassword,
                                                )
                                            }
                                            sx={{
                                                width: '100%',
                                                height: '25px',
                                                display: 'flex',
                                                '&:hover': {
                                                    cursor: 'pointer',
                                                    color: theme.palette
                                                        .textAccent.main,
                                                    backgroundColor:
                                                        'transparent',
                                                    background: 'none',
                                                },
                                            }}
                                        >
                                            <VisibilityOffIcon fontSize='small' />
                                        </CustomIconButton>
                                    )}
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Box sx={{ mt: 2 }}>
                        <CustomButton
                            type='submit'
                            color='primary'
                            variant='contained'
                            style={btnstyle}
                            fullWidth
                            disabled={
                                !isValidEmail ||
                                isValidInput ||
                                passwordsDontMatch ||
                                errors.length > 0
                            }
                            onClick={onClickResetPassword}
                        >
                            Save
                        </CustomButton>
                    </Box>

                    <Box sx={{ mt: 1.5 }}>
                        <Typography
                            sx={{
                                fontSize: '12px',
                                mb: 1,
                            }}
                        >
                            Password must:
                        </Typography>

                        <List
                            sx={{
                                p: 0,
                                m: 0,
                                '& .MuiListItem-root': { px: 0, py: 0.35 },
                            }}
                        >
                            <ListItem sx={{ fontSize: '12px' }}>
                                {pwLengthValidation ? (
                                    <DoneIcon
                                        sx={{
                                            fontSize: '14px',
                                            pr: 0.75,
                                            color: 'green',
                                        }}
                                    />
                                ) : (
                                    <CloseIcon
                                        sx={{
                                            fontSize: '14px',
                                            pr: 0.75,
                                            color: 'red',
                                        }}
                                    />
                                )}
                                Be a minimum of 8 characters
                            </ListItem>

                            <ListItem sx={{ fontSize: '12px' }}>
                                {lowerCaseValidation ? (
                                    <DoneIcon
                                        sx={{
                                            fontSize: '14px',
                                            pr: 0.75,
                                            color: 'green',
                                        }}
                                    />
                                ) : (
                                    <CloseIcon
                                        sx={{
                                            fontSize: '14px',
                                            pr: 0.75,
                                            color: 'red',
                                        }}
                                    />
                                )}
                                Include at least one lowercase letter (a-z)
                            </ListItem>

                            <ListItem sx={{ fontSize: '12px' }}>
                                {upperCaseValidation ? (
                                    <DoneIcon
                                        sx={{
                                            fontSize: '14px',
                                            pr: 0.75,
                                            color: 'green',
                                        }}
                                    />
                                ) : (
                                    <CloseIcon
                                        sx={{
                                            fontSize: '14px',
                                            pr: 0.75,
                                            color: 'red',
                                        }}
                                    />
                                )}
                                Include at least one uppercase letter (A-Z)
                            </ListItem>

                            <ListItem sx={{ fontSize: '12px' }}>
                                {numberValidation ? (
                                    <DoneIcon
                                        sx={{
                                            fontSize: '14px',
                                            pr: 0.75,
                                            color: 'green',
                                        }}
                                    />
                                ) : (
                                    <CloseIcon
                                        sx={{
                                            fontSize: '14px',
                                            pr: 0.75,
                                            color: 'red',
                                        }}
                                    />
                                )}
                                Include at least one number (0-9)
                            </ListItem>

                            <ListItem sx={{ fontSize: '12px' }}>
                                {characterValidation ? (
                                    <DoneIcon
                                        sx={{
                                            fontSize: '14px',
                                            pr: 0.75,
                                            color: 'green',
                                        }}
                                    />
                                ) : (
                                    <CloseIcon
                                        sx={{
                                            fontSize: '14px',
                                            pr: 0.75,
                                            color: 'red',
                                        }}
                                    />
                                )}
                                Include at least one special character (!@#?&)
                            </ListItem>
                        </List>
                    </Box>

                    <Box
                        sx={{
                            mt: { xs: 2.5, sm: 3 },
                            pb: { xs: 1.5, sm: 0 },
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Link target='_blank' to={IOS_APP_STORE_URL}>
                            <img
                                alt='appstore'
                                src='/AppleWhite.png'
                                style={{
                                    width: 'auto',
                                    height: isMobile ? '36px' : '40px',
                                }}
                            />
                        </Link>

                        {!isIOS && (
                            <Link target='_blank' to={ANDROID_PLAY_STORE_URL}>
                                <img
                                    alt='googleplay'
                                    src='/googleWhite.png'
                                    style={{
                                        width: 'auto',
                                        height: isMobile ? '36px' : '40px',
                                        padding: 0,
                                    }}
                                />
                            </Link>
                        )}
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}
